import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const Feature = ({ icon, title, description }) => (
  <div className="card" style={{ textAlign: 'center' }}>
    <div style={{ fontSize: '48px', marginBottom: '16px' }}>{icon}</div>
    <h3 style={{ marginBottom: '12px', color: 'var(--text)' }}>{title}</h3>
    <p style={{ color: 'var(--text-muted)', lineHeight: '1.6' }}>{description}</p>
  </div>
);

const PricingCard = ({ title, price, period, features, popular, buttonText, buttonLink }) => (
  <div 
    className="card" 
    style={{ 
      position: 'relative',
      borderColor: popular ? '#DC2626' : 'var(--border)',
      transform: popular ? 'scale(1.05)' : 'none'
    }}
  >
    {popular && (
      <div 
        style={{
          position: 'absolute',
          top: '-12px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#DC2626',
          color: 'white',
          padding: '6px 16px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: '700',
          textTransform: 'uppercase',
          letterSpacing: '1px'
        }}
      >
        Most Popular
      </div>
    )}
    <div style={{ textAlign: 'center', marginBottom: '24px' }}>
      <h3 style={{ marginBottom: '8px' }}>{title}</h3>
      <div style={{ fontSize: '48px', fontWeight: '800', color: popular ? '#DC2626' : 'var(--text)' }}>
        {price}
      </div>
      <div style={{ color: 'var(--text-muted)' }}>{period}</div>
    </div>
    <ul style={{ listStyle: 'none', marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {features.map((feature, idx) => (
        <li key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--text-muted)' }}>
          <span style={{ color: '#10B981' }}>✓</span> {feature}
        </li>
      ))}
    </ul>
    <Link 
      to={buttonLink} 
      className={`btn ${popular ? 'btn-primary' : 'btn-secondary'}`}
      style={{ width: '100%' }}
    >
      {buttonText}
    </Link>
  </div>
);

const Landing = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({ users: 0, emails: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      // Try to get real stats from API
      const response = await axios.get('/api/health');
      // If API doesn't provide stats, show "New Service" message
      setStats({ users: 0, emails: 0 });
    } catch (err) {
      setStats({ users: 0, emails: 0 });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fade-in">
      {/* Hero Section */}
      <section style={{ textAlign: 'center', padding: '80px 0' }}>
        <div 
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '12px',
            background: 'rgba(220, 38, 38, 0.1)',
            border: '1px solid rgba(220, 38, 38, 0.3)',
            padding: '8px 20px',
            borderRadius: '30px',
            marginBottom: '32px',
            fontSize: '14px',
            color: '#DC2626'
          }}
        >
          <span className="pulse" style={{ 
            width: '8px', 
            height: '8px', 
            background: '#DC2626', 
            borderRadius: '50%' 
          }}></span>
          Now with PGP Encryption & Crypto Payments
        </div>
        
        <h1 style={{ 
          fontSize: 'clamp(40px, 8vw, 72px)', 
          fontWeight: '800',
          marginBottom: '24px',
          lineHeight: '1.1'
        }}>
          Secure Email for
          <span style={{ 
            display: 'block',
            background: 'linear-gradient(135deg, #DC2626 0%, #EF4444 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}>Modern Privacy</span>
        </h1>
        
        <p style={{ 
          fontSize: '20px', 
          color: 'var(--text-muted)',
          maxWidth: '600px',
          margin: '0 auto 40px',
          lineHeight: '1.8'
        }}>
          Addison Mail provides anonymous, encrypted email with PGP support, 
          temporary addresses, and crypto payments. Your privacy, secured.
        </p>
        
        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
          {user ? (
            <Link to="/dashboard" className="btn btn-primary" style={{ fontSize: '16px', padding: '16px 32px' }}>
              📧 Go to Dashboard →
            </Link>
          ) : (
            <>
              <Link to="/register" className="btn btn-primary" style={{ fontSize: '16px', padding: '16px 32px' }}>
                🚀 Get Started Free
              </Link>
              <Link to="/tempmail" className="btn btn-ghost" style={{ fontSize: '16px', padding: '16px 32px' }}>
                🕐 Try Temp Mail
              </Link>
            </>
          )}
        </div>
        
        {/* Real Stats or New Service Badge */}
        <div style={{ 
          display: 'flex', 
          gap: '40px', 
          justifyContent: 'center', 
          marginTop: '60px',
          flexWrap: 'wrap'
        }}>
          {stats.users > 0 ? (
            <>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '36px', fontWeight: '800', color: '#DC2626' }}>{stats.users}</div>
                <div style={{ color: 'var(--text-muted)' }}>Active Users</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '36px', fontWeight: '800', color: '#DC2626' }}>{stats.emails}</div>
                <div style={{ color: 'var(--text-muted)' }}>Emails Sent</div>
              </div>
            </>
          ) : (
            <div 
              style={{
                background: 'rgba(220, 38, 38, 0.1)',
                border: '1px solid rgba(220, 38, 38, 0.3)',
                padding: '16px 32px',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}
            >
              <span style={{ fontSize: '24px' }}>🎉</span>
              <span style={{ color: 'var(--text)' }}>New Service - Be Among the First Users!</span>
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      <section style={{ padding: '60px 0' }}>
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <h2 style={{ fontSize: '36px', fontWeight: '700', marginBottom: '16px' }}>
            Everything You Need
          </h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '18px' }}>
            Professional email features with privacy at the core
          </p>
        </div>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '24px'
        }}>
          <Feature 
            icon="🔐"
            title="PGP Encryption"
            description="End-to-end encryption with OpenPGP. Generate keys, sign and encrypt emails securely."
          />
          <Feature 
            icon="🕐"
            title="Temp Mail"
            description="Create disposable email addresses instantly. No registration required for basic use."
          />
          <Feature 
            icon="💰"
            title="Crypto Payments"
            description="Pay with Bitcoin, Ethereum, or Solana. No credit cards, no personal info needed."
          />
          <Feature 
            icon="🌍"
            title="Tor Compatible"
            description="Full Tor hidden service support. Access your email anonymously from anywhere."
          />
          <Feature 
            icon="📱"
            title="Modern Interface"
            description="Clean, fast web interface. Works perfectly on desktop and mobile devices."
          />
          <Feature 
            icon="🔒"
            title="Zero Knowledge"
            description="We can't read your emails. Your private keys never leave your device."
          />
        </div>
      </section>

      {/* Pricing Section */}
      <section style={{ padding: '60px 0' }}>
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <h2 style={{ fontSize: '36px', fontWeight: '700', marginBottom: '16px' }}>
            Simple Pricing
          </h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '18px' }}>
            Pay with crypto, stay anonymous
          </p>
        </div>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '24px',
          maxWidth: '1000px',
          margin: '0 auto'
        }}>
          <PricingCard 
            title="Free"
            price="$0"
            period="Forever"
            features={[
              '10 emails/day',
              '1 email account',
              '3 temp aliases',
              'Basic support'
            ]}
            popular={false}
            buttonText="Get Started"
            buttonLink="/register"
          />
          <PricingCard 
            title="Pro"
            price="$5"
            period="/month"
            features={[
              'Unlimited emails',
              '5 email accounts',
              '20 temp aliases',
              'PGP encryption',
              'Priority support'
            ]}
            popular={true}
            buttonText="Upgrade to Pro"
            buttonLink="/upgrade"
          />
          <PricingCard 
            title="Enterprise"
            price="$20"
            period="/month"
            features={[
              'Everything in Pro',
              'Custom domain',
              'Unlimited aliases',
              'API access',
              'Dedicated support'
            ]}
            popular={false}
            buttonText="Contact Sales"
            buttonLink="mailto:sales@addisonelectronique.com"
          />
        </div>
      </section>

      {/* CTA Section */}
      <section style={{ padding: '80px 0', textAlign: 'center' }}>
        <div className="card" style={{ 
          background: 'linear-gradient(135deg, rgba(220, 38, 38, 0.1) 0%, rgba(0,0,0,0.3) 100%)',
          borderColor: 'rgba(220, 38, 38, 0.3)'
        }}>
          <h2 style={{ fontSize: '36px', fontWeight: '700', marginBottom: '16px' }}>
            Ready to Secure Your Communications?
          </h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '18px', marginBottom: '32px' }}>
            Join Addison Mail for private, encrypted email communication.
          </p>
          <Link to="/register" className="btn btn-primary" style={{ fontSize: '16px', padding: '16px 32px' }}>
            🚀 Create Free Account
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Landing;

